import React from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { can } from '../utils/permissions';
import { withQuery, jobPath } from '../nav/paths';
import BudgetView from '../components/BudgetView';
import BudgetAdminView from '../components/BudgetAdminView';
import JobBudgetView from '../components/JobBudgetView';
import InvoicesView from '../components/InvoicesView';
import CreateInvoicesView from '../components/CreateInvoicesView';
import InvoiceApprovalView from '../components/InvoiceApprovalView';

// The Jobs, Receivables and Projections screens, one per address. A ?job= (and
// for Receivables ?customer=) opens that job on the screen; links to another
// screen for one job go to the job page (/jobs/:jobNo/:tab).

export function NoAccess() {
  return (
    <div style={{ maxWidth: '600px', margin: '40px auto', padding: '0 16px', textAlign: 'center' }}>
      <div className="banner banner-warn" style={{ justifyContent: 'center' }}>
        You do not have access to this screen. Please contact your administrator.
      </div>
    </div>
  );
}

// Renders children only for a user with the capability.
export function RequireCapability({ capability, children }) {
  const { user } = useAuth();
  return can(user, capability) ? children : <NoAccess />;
}

const useJobParam = () => (useSearchParams()[0].get('job') || '').trim();

export function CreateInvoicesPage() {
  const navigate = useNavigate();
  const job = useJobParam();
  return (
    <RequireCapability capability="createInvoices">
      <CreateInvoicesView key={job} deepLinkJob={job}
        onOpenBudget={(j) => navigate(jobPath(j, 'budget'))} />
    </RequireCapability>
  );
}

export function InvoiceApprovalPage() {
  return (
    <RequireCapability capability="invoiceApproval">
      <InvoiceApprovalView />
    </RequireCapability>
  );
}

export function JobBudgetPage() {
  const navigate = useNavigate();
  const job = useJobParam();
  return (
    <RequireCapability capability="budget">
      <JobBudgetView expandJob={job}
        onOpenJob={(j) => navigate(jobPath(j, 'invoicing'))}
        onOpenReceivables={(j) => navigate(jobPath(j, 'receivables'))} />
    </RequireCapability>
  );
}

export function ReceivablesPage() {
  const [params] = useSearchParams();
  return (
    <RequireCapability capability="receivables">
      <InvoicesView deepLinkJob={(params.get('job') || '').trim()} deepLinkCustomer={(params.get('customer') || '').trim()} />
    </RequireCapability>
  );
}

export function ProjectionsPage() {
  return (
    <RequireCapability capability="projections">
      <BudgetView />
    </RequireCapability>
  );
}

export function ProjectionsAdminPage() {
  return (
    <RequireCapability capability="projectionsAdmin">
      <BudgetAdminView />
    </RequireCapability>
  );
}

// Old /navision-web?tab=...&job=...&customer=... links (bookmarks, A/R emails)
// go to the matching screen; with no tab, to the first screen the user can use.
const LEGACY_TABS = [
  { tab: 'invoices', path: '/receivables', capability: 'receivables' },
  { tab: 'createInvoices', path: '/jobs/invoicing', capability: 'createInvoices' },
  { tab: 'invoiceApproval', path: '/jobs/approval', capability: 'invoiceApproval' },
  { tab: 'budget', path: '/jobs/budget', capability: 'budget' },
  { tab: 'projections', path: '/projections', capability: 'projections' },
  { tab: 'projectionsAdmin', path: '/projections/admin', capability: 'projectionsAdmin' },
];

export function NavisionWebRedirect() {
  const { user } = useAuth();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  const target = LEGACY_TABS.find((t) => t.tab === tab && can(user, t.capability))
    || LEGACY_TABS.find((t) => can(user, t.capability));
  if (!target) return <NoAccess />;
  return <Navigate replace to={withQuery(target.path, { job: params.get('job'), customer: params.get('customer') })} />;
}
